// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/ExtraServiceForm.tsx
// 🔧 PURPOSE: IL MODULO DI UN SERVIZIO EXTRA: motivo di indisponibilità,
//             opzioni (orario, bottiglie, confezioni, varianti, giorni,
//             quantità, note), form del transfer e bottone finale.
//
//             Ne esiste una copia sola, usata dalla scheda dedicata
//             (/extras/:serviceId) e — per l'azione — dalla barra fissa in
//             fondo alla scheda. Due copie sarebbero due comportamenti che
//             divergono in silenzio.
//
//             Ogni VARIANTE (bottiglia, confezione, tipo di pulizia,
//             articolo di scorte e comfort) è una riga con la sua
//             miniatura: toccandola si apre la foto grande e la
//             descrizione, quando il gestore le ha caricate. Il prezzo lo
//             calcola src/lib/extraSelection.ts; qui si mostra.
//
//             Le bottiglie con la scheda del vino (`info`) aprono invece
//             <WineSheet>; quella `featured` va per prima, in una card in
//             evidenza, con lo stesso contatore delle altre. Lo spritz
//             (`info.kind: 'spritz'`) e il prosecco (`'prosecco'`) restano
//             righe normali: cambiano solo il link ("Scopri lo spritz" in
//             arancio, "Scopri il prosecco" in oro) e la scheda che apre.
// ============================================

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Coffee, Home as HomeIcon, Minus, Package, Plane, Plus, Sparkles, TrainFront, Wine } from 'lucide-react';
import type { AppExtra, ExtraCheckoutDetails, TransferOptions } from '../lib/guestApi';
import { guestAssetUrl } from '../lib/guestApi';
import { variantImageUrl } from '../lib/nonnaBasket';
import {
  bundlePiecesText,
  bundleVariantsOf,
  chosenKitDrink,
  compareAtOf,
  compareAtTotal,
  itemMaxQuantity,
  itemUnitPrice,
  itemVariantsOf,
  lowStockLeft,
  maxQuantityOf,
  notSellableInApp,
  stockOf,
  wineQtyOf,
  wineUnitPrice,
  wineVariantsOf,
  type ExtraSelection,
  type WineVariant,
} from '../lib/extraSelection';
import { quoteTransfer, stationPrice, type TransferSelection, type TransferDateLimits } from '../lib/transferPricing';
import DayChips, { dayRange, romeToday } from './DayChips';
import { customTaxiComplete, CUSTOM_TAXI_ID, transferLegChosen } from '../lib/extraSelection';
import { formatEuro } from './TouristTaxWidget';
import { intlLocale } from '../lib/locale';
import { EARLY_CHECKIN_ID, slotBlock } from '../lib/earlyCheckin';
import EarlyCheckinSlotNotes from './EarlyCheckinSlotNotes';
import WineSheet, { splitWineName, WINE_SERIF, wineFactsLine, wineHasInfo, wineSheetTheme } from './WineSheet';

/** Confezioni massime per acquisto: lo stesso tetto del server (app-extras.ts). */
const MAX_BUNDLE_PACKS = 10;

/** Giorni ammessi per gli input data del transfer. */
export type { TransferDateLimits };

export interface ExtraServiceFormProps {
  item: AppExtra;
  locale: string;
  selection: ExtraSelection;
  onChange: (patch: Partial<ExtraSelection>) => void;
  /** Prezzo d'anteprima già calcolato (src/lib/extraSelection.ts). */
  price: number;
  canBuy: boolean;
  busy?: boolean;
  requested: boolean;
  requestState?: 'sending' | 'sent' | 'error';
  /** Errore dell'ultima richiesta, già tradotto. */
  requestErrorText?: string | null;
  /** Solo transfer: con la selezione attuale si compra o si chiede? */
  transferAction?: 'buy' | 'request' | null;
  transferDateLimits?: TransferDateLimits;
  /** Il giorno di arrivo ("YYYY-MM-DD"): se è oggi, le fasce già passate del check-in anticipato si spengono. */
  checkInDate?: string;
  /** Il giorno di partenza: con l'arrivo, i giorni ammessi per il taxi su misura. */
  checkOutDate?: string;
  onBuy: () => void;
  onRequest: () => void;
  /** true = il bottone lo mette qualcun altro (la barra fissa della scheda). */
  hideAction?: boolean;
}

const LABEL = 'block text-[13px] font-semibold text-gray-600 dark:text-gray-300 mb-2';
const CHIP_ON = 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200';
const CHIP_OFF = 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900';
/** Le colonne del selettore della bottiglia, per quante sono le scelte (classi intere per Tailwind). */
const DRINK_COLS: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
const INPUT = 'w-full border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2.5 text-[15px] bg-white dark:bg-gray-900 text-gray-900 dark:text-white';

// ------------------------------------------------------------------
// Prezzo barrato, risparmio e "il più richiesto"
// ------------------------------------------------------------------

/**
 * Il prezzo con, davanti, il prezzo pieno barrato (piccolo e grigio) quando
 * il catalogo ne manda uno più alto: `€ 26,90 € 17,99`.
 *
 * `variant="contrast"` (la Home): prima il prezzo, poi il barrato in
 * nva-muted / stone-400. Il grigio di sempre sul crema e sull'avorio della
 * Home fa 2,2-2,5:1; così passa AA. Le misure le decide chi lo usa
 * (`className` sul prezzo, `compareClassName` sul barrato).
 */
export const PriceWithCompare: React.FC<{
  price: number;
  compareAt?: number | null;
  locale: string;
  variant?: 'default' | 'contrast';
  className?: string;
  compareClassName?: string;
}> = ({
  price, compareAt, locale, variant = 'default', className = '', compareClassName = '',
}) => {
  const full = typeof compareAt === 'number' && compareAt > price ? compareAt : null;
  if (variant === 'contrast') {
    return (
      <span className="inline-flex items-baseline gap-2 flex-wrap">
        <span className={`tabular-nums whitespace-nowrap ${className}`}>{formatEuro(price, locale)}</span>
        {full !== null && (
          <del className={`font-medium text-nva-muted dark:text-stone-400 tabular-nums whitespace-nowrap ${compareClassName}`}>
            {formatEuro(full, locale)}
          </del>
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-baseline gap-1.5 flex-wrap justify-end">
      {full !== null && (
        <del className="text-[12px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">{formatEuro(full, locale)}</del>
      )}
      <span className="tabular-nums">{formatEuro(price, locale)}</span>
    </span>
  );
};

/**
 * La pillola verde "Risparmi € 8,91". `variant="text"` (la Home): solo la
 * scritta, in terracotta (5:1 sull'avorio della card, #F0A868 in scuro).
 */
export const SavingsPill: React.FC<{ amount: number; locale: string; className?: string; variant?: 'pill' | 'text' }> = ({
  amount, locale, className = '', variant = 'pill',
}) => {
  const { t } = useTranslation();
  const saved = Math.round(amount * 100) / 100;
  if (!(saved > 0)) return null;
  if (variant === 'text') {
    return (
      <span className={`text-[12px] font-bold text-nva-due-btn dark:text-nva-due-ink-dark whitespace-nowrap tabular-nums ${className}`}>
        {t('extras.price.save', { amount: formatEuro(saved, locale) })}
      </span>
    );
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold whitespace-nowrap tabular-nums ${className}`}>
      {t('extras.price.save', { amount: formatEuro(saved, locale) })}
    </span>
  );
};

/** Il badge "⭐ Il più richiesto" dell'articolo evidenziato dal catalogo. */
export const PopularBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation();
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[11px] font-extrabold uppercase tracking-wide shadow-sm ${className}`}>
      <span aria-hidden="true">⭐</span> {t('extras.price.popular')}
    </span>
  );
};

/**
 * Il prezzo di una variante (articolo, confezione) nella sua riga: barrato
 * + prezzo, e sotto la pillola del risparmio quando c'è.
 */
const VariantPrice: React.FC<{ price: number; compareAtPrice?: number; discountPercentage?: number; locale: string }> = ({
  price, compareAtPrice, discountPercentage, locale,
}) => {
  // Articolo scontato (l'acqua a −10%): come le bottiglie, listino barrato,
  // prezzo scontato in verde e "−10%". È il prezzo che il server incassa.
  if (discountPercentage && discountPercentage > 0) {
    const unit = itemUnitPrice({ price, discountPercentage });
    return (
      <span className="flex items-center gap-1.5 flex-wrap justify-end">
        <del className="line-through opacity-60 font-medium text-[13px]">{formatEuro(price, locale)}</del>
        <span className="text-emerald-600 dark:text-emerald-400">{formatEuro(unit, locale)}</span>
        <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">−{discountPercentage}%</span>
      </span>
    );
  }
  const full = compareAtOf({ price, compareAtPrice });
  return (
    <span className="flex flex-col items-end gap-1">
      <PriceWithCompare price={price} compareAt={full} locale={locale} />
      {full !== null && <SavingsPill amount={full - price} locale={locale} />}
    </span>
  );
};

/**
 * Il prezzo e la scorta. Il NUMERO è lo stesso — lo calcola `previewPrice` —
 * la posizione cambia perché cambia il layout.
 */
export const ExtraPriceTag: React.FC<{
  item: AppExtra;
  price: number;
  locale: string;
  align?: 'left' | 'right';
  /** Prezzo pieno della scelta attuale (compareAtTotal), da barrare. */
  compareAt?: number | null;
}> = ({ item, price, locale, align = 'right', compareAt }) => {
  const { t } = useTranslation();
  const transfer = item.options.transfer;
  const wines = wineVariantsOf(item);
  const bundles = bundleVariantsOf(item);
  const items = itemVariantsOf(item);
  const stock = stockOf(item);

  // Prezzo nascosto finché non significa niente: transfer senza tratta,
  // bottiglia senza bottiglie, confezione o articolo non ancora scelti.
  const hidePrice =
    item.priceType === 'on_request'
    || notSellableInApp(item)
    || ((!!transfer || !!wines || !!bundles || !!items) && !(price > 0));
  if (hidePrice) return null;

  const stockLabel =
    stock === null ? null
      : stock <= 0 ? (item.reason === 'sold_out' ? null : t('extras.inventory.soldOut'))
        : stock === 1 ? t('extras.inventory.last')
          : t('extras.inventory.available', { n: stock });

  return (
    <div className={`flex-shrink-0 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <span className="block text-lg font-bold text-gray-900 dark:text-white tabular-nums">
        <PriceWithCompare price={price} compareAt={compareAt} locale={locale} />
      </span>
      {typeof compareAt === 'number' && compareAt > price && (
        <SavingsPill amount={compareAt - price} locale={locale} className="mt-1" />
      )}
      {stockLabel && (
        <span className={`block text-xs mt-0.5 ${stock === 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {stockLabel}
        </span>
      )}
    </div>
  );
};

/** La stima di consegna, la STESSA che legge chi compra dal tablet. */
const DeliveryEstimate: React.FC = () => {
  const { t } = useTranslation();
  let hour = 12;
  try {
    hour = Number(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', hour: '2-digit', hour12: false })
        .format(new Date()),
    );
  } catch { /* fallback mezzogiorno */ }
  const daytime = hour >= 8 && hour < 18;
  return (
    <p className="mt-3 text-[13px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-xl px-3 py-2.5 leading-relaxed">
      {daytime ? t('extras.delivery.day') : t('extras.delivery.night')}
    </p>
  );
};

// ------------------------------------------------------------------
// Varianti: miniatura + riga + pannello con foto e descrizione
// ------------------------------------------------------------------

type VariantKind = 'wine' | 'bundle' | 'cleaning' | 'item';

const KIND_ICON = { wine: Wine, bundle: Coffee, cleaning: Sparkles, item: Package };
const KIND_BG = {
  wine: 'bg-gradient-to-br from-rose-100 to-red-200 dark:from-rose-900/40 dark:to-red-900/30 text-rose-600 dark:text-rose-300',
  bundle: 'bg-gradient-to-br from-amber-100 to-orange-200 dark:from-amber-900/40 dark:to-orange-900/30 text-amber-600 dark:text-amber-300',
  cleaning: 'bg-gradient-to-br from-cyan-100 to-teal-200 dark:from-cyan-900/40 dark:to-teal-900/30 text-cyan-600 dark:text-cyan-300',
  item: 'bg-gradient-to-br from-violet-100 to-indigo-200 dark:from-violet-900/40 dark:to-indigo-900/30 text-violet-600 dark:text-violet-300',
};

const VariantPhoto: React.FC<{ imageUrl?: string; kind: VariantKind; alt: string; size: 'thumb' | 'tall' | 'large' }> = ({
  imageUrl, kind, alt, size,
}) => {
  const src = guestAssetUrl(imageUrl);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  const Icon = KIND_ICON[kind];
  const box = size === 'thumb'
    ? 'w-14 h-14 rounded-xl'
    : size === 'tall' ? 'w-[84px] h-[115px] rounded-xl' : 'w-full aspect-[16/9] rounded-xl';
  if (!src || failed) {
    return (
      <div className={`${box} ${KIND_BG[kind]} flex items-center justify-center flex-shrink-0`}>
        <Icon className={size === 'thumb' ? 'h-6 w-6' : size === 'tall' ? 'h-8 w-8' : 'h-12 w-12'} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <div className={`${box} ${KIND_BG[kind]} overflow-hidden flex-shrink-0 relative`}>
      <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className="absolute inset-0 w-full h-full object-cover" />
    </div>
  );
};

interface VariantRowProps {
  kind: VariantKind;
  name: string;
  subtitle?: React.ReactNode;
  price?: React.ReactNode;
  imageUrl?: string;
  description?: string;
  selected: boolean;
  disabled?: boolean;
  /** Tocco sulla riga: seleziona (scelta singola) oppure apre/chiude i dettagli. */
  onTap: () => void;
  /** Il controllo a destra: contatore (bottiglie, confezioni) o spunta. */
  control?: React.ReactNode;
  open: boolean;
  /** true = l'articolo "più richiesto": badge sopra il nome e bordo caldo. */
  popular?: boolean;
  /** Bottiglia con la scheda del vino: il tocco apre la scheda al posto dei dettagli. */
  onDiscover?: () => void;
  /** Il link della scheda ("Scopri il vino" / "lo spritz" / "il prosecco") e il suo colore. */
  discoverLabel?: string;
  discoverClassName?: string;
  /**
   * true = nei dettagli c'è il TESTO al posto della foto grande (le confezioni
   * di "Caffè & Brioche": cosa arriva, come si consegna, come si prepara; i
   * kit: cosa si compra e come arriva). Senza testo resta la foto, come prima.
   */
  textDetails?: boolean;
}

const VariantRow: React.FC<VariantRowProps> = ({
  kind, name, subtitle, price, imageUrl, description, selected, disabled, onTap, control, open, popular, onDiscover,
  discoverLabel, discoverClassName, textDetails,
}) => {
  const { t } = useTranslation();
  const hasDetails = !onDiscover && (!!description || !!imageUrl);
  // Con il testo della variante, la foto grande nei dettagli non serve più.
  const showLargeImage = !!imageUrl && !(textDetails && description);
  const border = selected
    ? 'border-amber-500'
    : popular
      ? 'border-amber-300 dark:border-amber-700/70'
      : 'border-gray-200 dark:border-gray-700';
  const bg = popular && !selected ? 'bg-amber-50/60 dark:bg-amber-900/10' : 'bg-white dark:bg-gray-900';
  return (
    <div className={`rounded-2xl border-2 transition-colors ${border} ${disabled ? 'opacity-50' : ''} ${bg} overflow-hidden`}>
      <div className="flex items-center gap-3 p-2.5">
        <button
          type="button"
          onClick={onDiscover || onTap}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
          aria-expanded={onDiscover ? undefined : open}
          aria-haspopup={onDiscover ? 'dialog' : undefined}
        >
          <VariantPhoto imageUrl={imageUrl} kind={kind} alt={name} size="thumb" />
          <span className="min-w-0 flex-1">
            {popular && <PopularBadge className="mb-1" />}
            <span className="block text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">{name}</span>
            {subtitle && <span className="block text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</span>}
            {hasDetails && (
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600 dark:text-amber-400 mt-1">
                {open ? t('extras.variant.hide') : t('extras.variant.details')}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
              </span>
            )}
            {onDiscover && (
              <span className={`inline-flex items-center gap-0.5 text-xs font-semibold mt-1 ${discoverClassName || 'text-[#7a1f2b] dark:text-rose-300'}`}>
                {discoverLabel || t('extras.wine.discover')}
                <ChevronRight className="h-3.5 w-3.5" />
              </span>
            )}
          </span>
        </button>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {price && <span className="text-[15px] font-bold text-gray-900 dark:text-white tabular-nums">{price}</span>}
          {control}
        </div>
      </div>
      {open && hasDetails && (
        <div className="px-2.5 pb-3 pt-0.5 space-y-2">
          {showLargeImage && <VariantPhoto imageUrl={imageUrl} kind={kind} alt={name} size="large" />}
          {description && (
            // `whitespace-pre-line`: il testo dei kit va a capo prima della consegna.
            <p className={textDetails ? 'text-[15px] text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line bg-amber-50/70 dark:bg-amber-900/10 rounded-xl px-3 py-2.5' : 'text-[14px] text-gray-600 dark:text-gray-300 leading-relaxed'}>
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * La bottiglia `featured` di "Vino & Prosecco": card in evidenza col bordo
 * color vino, foto verticale, pillola (info.badge), nome in serif e la riga
 * "2022 · 14,5% · …". Sotto, prezzo e contatore: gli STESSI delle altre
 * bottiglie, passati da fuori.
 */
const FeaturedWineCard: React.FC<{
  wine: WineVariant;
  locale: string;
  selected: boolean;
  price: React.ReactNode;
  /** Il contatore (o "Esaurita"). */
  control: React.ReactNode;
  /** Apre la scheda del vino; assente se il vino non ha la scheda. */
  onDiscover?: () => void;
}> = ({ wine, locale, selected, price, control, onDiscover }) => {
  const { t } = useTranslation();
  const { head, rest } = splitWineName(wine.name);
  const facts = wineFactsLine(wine.info, locale);
  const badge = wine.info?.badge;
  const theme = wineSheetTheme(wine);
  const body = (
    <>
      <VariantPhoto imageUrl={wine.imageUrl} kind="wine" alt={wine.name} size="tall" />
      <span className="min-w-0 flex-1 py-0.5">
        {badge && (
          <span className="inline-block mb-1.5 px-2.5 py-0.5 rounded-full bg-[#fdf2f4] dark:bg-rose-900/30 text-[#7a1f2b] dark:text-rose-200 text-[11px] font-bold">
            {badge}
          </span>
        )}
        <span className="block text-[26px] leading-[1.1] text-gray-900 dark:text-white" style={WINE_SERIF}>{head}</span>
        {rest && <span className="block mt-0.5 text-[15px] font-semibold text-gray-800 dark:text-gray-100 leading-snug">{rest}</span>}
        {facts && <span className="block mt-1 text-[12px] text-gray-500 dark:text-gray-400">{facts}</span>}
        {!wine.isAvailable && <span className="block mt-1 text-[13px] text-gray-500 dark:text-gray-400">{t(wine.soldOut ? 'extras.stock.soldOut' : 'extras.wine.soldOut')}</span>}
        {lowStockLeft(wine) !== null && <span className="block mt-1 text-[13px] font-semibold text-amber-700 dark:text-amber-300">{t('extras.stock.last', { count: lowStockLeft(wine) })}</span>}
        {onDiscover && (
          <span className={`inline-flex items-center gap-0.5 mt-2 text-[13px] font-semibold ${theme.discoverText}`}>
            {t(theme.discoverKey)}
            <ChevronRight className="h-4 w-4" />
          </span>
        )}
      </span>
    </>
  );
  return (
    <div
      className={`rounded-2xl border-2 border-[#7a1f2b] dark:border-rose-700 bg-white dark:bg-gray-900 overflow-hidden transition-shadow ${
        selected ? 'shadow-md shadow-[#7a1f2b]/15' : ''
      } ${wine.isAvailable ? '' : 'opacity-60'}`}
    >
      {onDiscover ? (
        <button type="button" onClick={onDiscover} aria-haspopup="dialog" className="w-full flex items-start gap-3 p-3 text-left">
          {body}
        </button>
      ) : (
        <div className="flex items-start gap-3 p-3">{body}</div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-[#7a1f2b]/15 dark:border-rose-900/40 bg-[#fdf2f4]/60 dark:bg-rose-950/20">
        <span className="text-[16px] font-bold text-gray-900 dark:text-white tabular-nums">{price}</span>
        {control}
      </div>
    </div>
  );
};

const Stepper: React.FC<{
  value: number; min: number; max: number; onChange: (n: number) => void; labelMinus: string; labelPlus: string;
}> = ({ value, min, max, onChange, labelMinus, labelPlus }) => (
  <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
    <button type="button" aria-label={labelMinus} disabled={value <= min} onClick={() => onChange(value - 1)} className="w-9 h-9 flex items-center justify-center text-gray-700 dark:text-gray-200 disabled:opacity-30">
      <Minus className="h-4 w-4" />
    </button>
    <span className="w-8 text-center text-[15px] font-bold text-gray-900 dark:text-white tabular-nums bg-gray-50 dark:bg-gray-800 h-9 leading-9">{value}</span>
    <button type="button" aria-label={labelPlus} disabled={value >= max} onClick={() => onChange(value + 1)} className="w-9 h-9 flex items-center justify-center text-gray-700 dark:text-gray-200 disabled:opacity-30">
      <Plus className="h-4 w-4" />
    </button>
  </div>
);

const SelectedMark: React.FC<{ on: boolean }> = ({ on }) => (
  <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center ${on ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
    {on && <Check className="h-4 w-4" strokeWidth={3} />}
  </span>
);

// ------------------------------------------------------------------
// Il modulo
// ------------------------------------------------------------------

const ExtraServiceForm: React.FC<ExtraServiceFormProps> = (props) => {
  const { item, locale, selection, onChange, hideAction } = props;
  const { t } = useTranslation();
  const opts = item.options || {};
  const transfer = opts.transfer;
  const wineVariants = wineVariantsOf(item);
  const bundleVariants = bundleVariantsOf(item);
  const itemVariants = itemVariantsOf(item);
  const notSellable = notSellableInApp(item);
  const multipliedByStay =
    item.priceType === 'per_person' || item.priceType === 'per_night' || item.priceType === 'per_person_night';
  const packs = Math.max(1, Number(selection.quantity) || 1);
  // Quale variante ha il pannello aperto (una alla volta: la pagina resta corta).
  const [openVariant, setOpenVariant] = useState<string | null>(null);
  const toggleOpen = (id: string) => setOpenVariant((cur) => (cur === id ? null : id));
  // La bottiglia di cui è aperta la scheda del vino (null = chiusa).
  const [sheetWineId, setSheetWineId] = useState<string | null>(null);
  const sheetWine = (wineVariants || []).filter((v) => v.id === sheetWineId)[0] || null;
  // Le bottiglie in evidenza vanno per prime; le altre restano nell'ordine del catalogo.
  const orderedWines = wineVariants
    ? wineVariants.filter((v) => v.featured).concat(wineVariants.filter((v) => !v.featured))
    : null;
  // Il tetto delle unità: 10, o quanti ne restano dell'articolo scelto.
  const chosenItem = (itemVariants || []).filter((v) => v.id === selection.itemVariant)[0];
  const itemUnitsMax = itemMaxQuantity(chosenItem);
  // Il Cestino della Nonna: la bottiglia a scelta (quella dell'ospite, o la
  // predefinita del catalogo).
  const drinkOptions = chosenItem && chosenItem.drinkChoice ? chosenItem.drinkChoice.options : [];
  const chosenDrink = chosenKitDrink(chosenItem, selection);
  const itemUnits = Math.max(1, Math.min(itemUnitsMax, Number(selection.quantity) || 1));
  // Confezioni: 10, o quante ne restano di quella scelta.
  const chosenBundle = (bundleVariants || []).filter((b) => b.id === selection.bundleVariant)[0];
  const bundlePacksMax = chosenBundle && typeof chosenBundle.stockLeft === 'number'
    ? Math.max(1, Math.min(MAX_BUNDLE_PACKS, chosenBundle.stockLeft))
    : MAX_BUNDLE_PACKS;
  // Scorta fisica (la culla): il tetto dello stepper generico non è il 10 di
  // sempre ma i pezzi liberi per le date del soggiorno, come li conta il
  // server. `stock` è null per tutti gli altri servizi.
  const stock = stockOf(item);
  const quantityMax = maxQuantityOf(item);
  const quantityValue = Math.max(1, Math.min(quantityMax, Number(selection.quantity) || 1));

  /**
   * Con pochi articoli si parte dal primo: un tocco in meno e il prezzo si
   * vede subito. Con una lista lunga (la biancheria ne ha dieci) non si
   * preseleziona niente, altrimenti si compra il primo senza averlo scelto.
   */
  const presetItemId = itemVariants && itemVariants.length <= 3 ? itemVariants[0].id : null;
  useEffect(() => {
    if (presetItemId && !selection.itemVariant) onChange({ itemVariant: presetItemId });
  }, [presetItemId, selection.itemVariant, onChange]);

  /**
   * Cambia la quantità di UNA bottiglia. La lista si ricostruisce sempre
   * nell'ordine del catalogo: così il riepilogo ("2× Primitivo, 1× Rosato")
   * non si riordina sotto le dita a ogni tocco.
   */
  const setWineQty = (id: string, qty: number) => {
    const wine = (wineVariants || []).filter((v) => v.id === id)[0];
    const cap = wine && typeof wine.stockLeft === 'number' ? Math.min(99, wine.stockLeft) : 99;
    const clamped = Math.max(0, Math.min(cap, qty));
    const next: Array<{ id: string; qty: number }> = [];
    (wineVariants || []).forEach((v) => {
      const q = v.id === id ? clamped : wineQtyOf(selection, v.id);
      if (q > 0) next.push({ id: v.id, qty: q });
    });
    onChange({ wineSelections: next });
  };

  const toggleDate = (day: string) => {
    const current = selection.cleaningDates || [];
    const next = current.indexOf(day) >= 0 ? current.filter((d) => d !== day) : current.concat([day]);
    onChange({ cleaningDates: next });
  };

  return (
    <>
      {/* Badge: già preso, oppure il motivo per cui adesso non si può */}
      {item.alreadyPurchased && (
        <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-[13px] font-semibold">
          <Check className="h-4 w-4" /> {t('extras.badges.alreadyPurchased')}
        </span>
      )}
      {!item.available && !item.alreadyPurchased && item.reason && !(transfer && item.canRequest) && (
        <p className="mt-3 text-[13px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-xl px-3 py-2.5 leading-relaxed">
          {/* Magazzino: un prodotto finito è "Esaurito", non "per le tue date". */}
          {item.reason === 'sold_out' && opts.soldOut ? t('extras.stock.soldOutReason') : t(`extras.reasons.${item.reason}`, {
            defaultValue: t('extras.reasons.default'),
            hours: item.minAdvanceHours,
            from: item.windowFrom,
            to: item.windowTo,
            nights: item.nightsRemaining,
          })}
        </p>
      )}

      {opts.deliveryEstimate && item.available && !item.alreadyPurchased && <DeliveryEstimate />}

      {/* --- Taxi su misura: una RICHIESTA (meta, giorno, ora, persone);
          il gestore risponde con disponibilità e prezzo. --- */}
      {item.serviceId === CUSTOM_TAXI_ID && item.canRequest && !item.alreadyPurchased && (
        <CustomTaxiForm
          selection={selection}
          onChange={onChange}
          stayFrom={props.checkInDate}
          stayTo={props.checkOutDate}
        />
      )}

      {/* --- Transfer: il form resta lo STESSO sia che si compri sia che si
          mandi una richiesta. Cambia solo il bottone in fondo. --- */}
      {transfer && (item.available || item.canRequest) && !item.alreadyPurchased && (
        <TransferForm
          serviceId={item.serviceId}
          options={transfer}
          selection={selection as TransferSelection}
          onChange={onChange}
          locale={locale}
          price={props.price}
          dateLimits={props.transferDateLimits}
        />
      )}

      {/* --- Opzioni, solo quando servono davvero --- */}
      {item.available && !notSellable && !transfer && (
        <div className="mt-4 space-y-4">
          {opts.requiresTimeSelection && opts.timeSlotOptions && opts.timeSlotOptions.length > 0 && (
            <div>
              <p className={LABEL}>{t('extras.form.time')}</p>
              {/* Le stesse fasce, agli stessi prezzi e alle stesse condizioni
                  del check-in online: una fascia non disponibile resta
                  visibile ma spenta, e se si arriva OGGI le fasce già
                  passate non si possono più comprare (ora di Roma, non
                  quella del telefono). Il check-in anticipato si compra
                  fino a 30 minuti prima, e mai prima che l'appartamento si
                  liberi (late check-out di chi parte): lib/earlyCheckin. */}
              <div className="flex flex-wrap gap-2">
                {opts.timeSlotOptions.map((slot) => {
                  const block = slotBlock(slot, { serviceId: item.serviceId, checkIn: props.checkInDate, now: new Date() });
                  const usable = block === null;
                  const on = selection.selectedTime === slot.time;
                  return (
                    <button
                      key={slot.time}
                      type="button"
                      disabled={!usable}
                      onClick={() => onChange({ selectedTime: on ? undefined : slot.time })}
                      className={`px-3.5 py-2 rounded-xl text-[15px] font-semibold border-2 disabled:opacity-45 ${on && usable ? CHIP_ON : CHIP_OFF}`}
                    >
                      <span className="tabular-nums">{slot.time}</span>
                      <span className={`ml-2 text-[13px] ${usable ? 'text-gray-500 dark:text-gray-400' : 'line-through text-gray-400'}`}>{formatEuro(slot.price, locale)}</span>
                      {(block === 'unavailable' || block === 'apartment_busy') && <span className="ml-2 text-[11px] font-bold text-red-500 uppercase">{t('extras.form.slotUnavailable')}</span>}
                      {block === 'past' && <span className="ml-2 text-[11px] font-bold text-orange-500 uppercase">{t('extras.form.timePassed')}</span>}
                      {block === 'too_close' && <span className="ml-2 text-[11px] font-bold text-orange-500 uppercase">{t('extras.form.slotTooClose')}</span>}
                    </button>
                  );
                })}
              </div>
              {item.serviceId === EARLY_CHECKIN_ID && (
                <EarlyCheckinSlotNotes item={item} checkIn={props.checkInDate} />
              )}
            </div>
          )}

          {/* --- Bottiglie: quantità per variante, come sul tablet. La
              bottiglia `featured` va per prima, nella card in evidenza; chi
              ha la scheda del vino (`info`) la apre con "Scopri il vino"
              (spritz e prosecco, righe normali, col loro link). --- */}
          {wineVariants && orderedWines && (
            <div>
              <p className={LABEL}>{t('extras.form.bottles')}</p>
              <div className="space-y-2">
                {orderedWines.map((v) => {
                  const qty = wineQtyOf(selection, v.id);
                  const unit = wineUnitPrice(v);
                  const discounted = !!v.discountPercentage && v.discountPercentage > 0;
                  const onDiscover = wineHasInfo(v) ? () => setSheetWineId(v.id) : undefined;
                  const sheetTheme = wineSheetTheme(v);
                  const priceNode = (justify: string) => (
                    <span className={`flex items-center gap-1.5 flex-wrap ${justify}`}>
                      {discounted && <span className="line-through opacity-60 font-medium text-[13px]">{formatEuro(v.price, locale)}</span>}
                      <span className={discounted ? 'text-emerald-600 dark:text-emerald-400' : ''}>{formatEuro(unit, locale)}</span>
                      {discounted && <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">−{v.discountPercentage}%</span>}
                    </span>
                  );
                  // Magazzino: mai più bottiglie di quante ne restano.
                  const wineMax = typeof v.stockLeft === 'number' ? Math.max(0, Math.min(99, v.stockLeft)) : 99;
                  const stepper = v.isAvailable ? (
                    <Stepper
                      value={qty}
                      min={0}
                      max={wineMax}
                      onChange={(n) => setWineQty(v.id, n)}
                      labelMinus={t('extras.wine.removeOne', { name: v.name })}
                      labelPlus={t('extras.wine.addOne', { name: v.name })}
                    />
                  ) : undefined;
                  if (v.featured) {
                    return (
                      <FeaturedWineCard
                        key={v.id}
                        wine={v}
                        locale={locale}
                        selected={qty > 0}
                        price={priceNode('justify-start')}
                        control={stepper}
                        onDiscover={onDiscover}
                      />
                    );
                  }
                  return (
                    <VariantRow
                      key={v.id}
                      kind="wine"
                      name={v.name}
                      imageUrl={v.imageUrl}
                      description={v.description}
                      selected={qty > 0}
                      disabled={!v.isAvailable}
                      open={openVariant === v.id}
                      onTap={() => toggleOpen(v.id)}
                      onDiscover={onDiscover}
                      discoverLabel={t(sheetTheme.discoverKey)}
                      discoverClassName={sheetTheme.discoverText}
                      subtitle={!v.isAvailable
                        ? t(v.soldOut ? 'extras.stock.soldOut' : 'extras.wine.soldOut')
                        : lowStockLeft(v) !== null ? t('extras.stock.last', { count: lowStockLeft(v) }) : undefined}
                      price={priceNode('justify-end')}
                      control={stepper}
                    />
                  );
                })}
              </div>
              <WineSheet
                wine={sheetWine}
                locale={locale}
                available={!!sheetWine && sheetWine.isAvailable}
                onClose={() => setSheetWineId(null)}
                onAdd={() => { if (sheetWine) setWineQty(sheetWine.id, wineQtyOf(selection, sheetWine.id) + 1); }}
              />
            </div>
          )}

          {/* --- Confezioni "Caffè & Brioche": formato + quante confezioni. --- */}
          {bundleVariants && (
            <>
              <div>
                <p className={LABEL}>{t('extras.form.bundle')}</p>
                <div className="space-y-2">
                  {bundleVariants.map((b) => {
                    const on = selection.bundleVariant === b.id;
                    const low = lowStockLeft(b);
                    return (
                      <VariantRow
                        key={b.id}
                        kind="bundle"
                        // Senza le parti a zero: "7 caffè", "4 brioche".
                        name={bundlePiecesText(t, b.coffeeQty, b.briocheQty)}
                        subtitle={low !== null ? t('extras.stock.last', { count: low }) : undefined}
                        imageUrl={b.imageUrl}
                        description={b.description}
                        textDetails
                        selected={on}
                        open={openVariant === b.id}
                        onTap={() => { onChange({ bundleVariant: b.id }); if (!on) setOpenVariant(b.id); else toggleOpen(b.id); }}
                        price={<VariantPrice price={b.price} compareAtPrice={b.compareAtPrice} locale={locale} />}
                        control={<SelectedMark on={on} />}
                      />
                    );
                  })}
                  {/* Magazzino: le confezioni finite, visibili ma spente. */}
                  {(opts.soldOutBundleVariants || []).map((b) => (
                    <VariantRow
                      key={`sold-${b.id}`}
                      kind="bundle"
                      name={bundlePiecesText(t, b.coffeeQty, b.briocheQty)}
                      subtitle={t('extras.stock.soldOut')}
                      imageUrl={b.imageUrl}
                      selected={false}
                      disabled
                      open={false}
                      onTap={() => undefined}
                      price={<VariantPrice price={b.price} locale={locale} />}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className={`${LABEL} mb-0`}>{t('extras.form.packs')}</p>
                <Stepper
                  value={Math.min(packs, bundlePacksMax)}
                  min={1}
                  max={bundlePacksMax}
                  onChange={(n) => onChange({ quantity: Math.min(bundlePacksMax, Math.max(1, n)) })}
                  labelMinus={t('extras.bundle.removeOne')}
                  labelPlus={t('extras.bundle.addOne')}
                />
              </div>
            </>
          )}

          {/* --- Articoli: se ne sceglie UNO e si dice quanti (carta, igiene,
              kit, biancheria). Il prezzo è quello dell'articolo. --- */}
          {itemVariants && (
            <>
              <div>
                <p className={LABEL}>{t('extras.form.chooseItem')}</p>
                <div className="space-y-2">
                  {itemVariants.map((v) => {
                    const on = selection.itemVariant === v.id;
                    const low = lowStockLeft(v);
                    return (
                      <React.Fragment key={v.id}>
                      <VariantRow
                        kind="item"
                        name={v.name}
                        subtitle={low !== null
                          ? <>{v.description ? <>{v.description} · </> : null}<span className="font-semibold text-amber-700 dark:text-amber-300">{t('extras.stock.last', { count: low })}</span></>
                          : v.description}
                        imageUrl={variantImageUrl(item.serviceId, v.id, v.imageUrl)}
                        // I kit: il testo (cosa si compra, come arriva) al
                        // posto della foto grande; la miniatura resta. Senza
                        // testo (channel manager vecchio) resta la foto.
                        description={v.detailText}
                        textDetails
                        selected={on}
                        open={openVariant === v.id}
                        onTap={() => { onChange({ itemVariant: v.id }); if (!on) setOpenVariant(v.id); else toggleOpen(v.id); }}
                        price={<VariantPrice price={v.price} compareAtPrice={v.compareAtPrice} discountPercentage={v.discountPercentage} locale={locale} />}
                        popular={v.highlight === 'popular'}
                        control={<SelectedMark on={on} />}
                      />
                      {/* Il Cestino: "Scegli la bottiglia: Rosso · Bianco · Prosecco ·
                          Spritz", subito sotto, fra quelle in vendita adesso.
                          Il prezzo non cambia. */}
                      {on && drinkOptions.length > 0 && (
                        <div className="px-1 pt-1 pb-1.5">
                          <p id="kit-drink-label" className={LABEL}>{t('extras.basket.chooseDrink')}</p>
                          <div
                            role="radiogroup"
                            aria-labelledby="kit-drink-label"
                            className={`grid gap-1.5 ${DRINK_COLS[Math.min(4, drinkOptions.length)] || 'grid-cols-4'}`}
                          >
                            {drinkOptions.map((o) => {
                              const picked = !!chosenDrink && chosenDrink.id === o.id;
                              return (
                                <button
                                  key={o.id}
                                  type="button"
                                  role="radio"
                                  aria-checked={picked}
                                  onClick={() => onChange({ kitDrink: o.id })}
                                  className={`min-h-[44px] px-1 rounded-xl border-2 text-[14px] font-semibold leading-tight ${picked ? CHIP_ON : CHIP_OFF}`}
                                >
                                  {t(`extras.basket.drink.${o.kind}`, { defaultValue: o.name })}
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-1.5 text-[12px] text-gray-500 dark:text-gray-400">{t('extras.upsell.adultsOnly')}</p>
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {/* Magazzino: gli articoli finiti, visibili ma spenti. */}
                  {(opts.soldOutItemVariants || []).map((v) => (
                    <VariantRow
                      key={`sold-${v.id}`}
                      kind="item"
                      name={v.name}
                      subtitle={t('extras.stock.soldOut')}
                      imageUrl={variantImageUrl(item.serviceId, v.id, v.imageUrl)}
                      selected={false}
                      disabled
                      open={false}
                      onTap={() => undefined}
                      price={<VariantPrice price={v.price} discountPercentage={v.discountPercentage} locale={locale} />}
                    />
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <p className={`${LABEL} mb-0`}>{t('extras.form.quantity')}</p>
                  <Stepper
                    value={itemUnits}
                    min={1}
                    max={itemUnitsMax}
                    onChange={(n) => onChange({ quantity: Math.min(itemUnitsMax, Math.max(1, n)) })}
                    labelMinus={t('common.decrease')}
                    labelPlus={t('common.increase')}
                  />
                </div>
                {/* Il conto fatto sotto le dita: prezzo dell'articolo per le
                    unità, così non c'è da fidarsi a memoria. */}
                {props.price > 0 && (
                  <p className="mt-1.5 text-right text-[15px] font-bold text-gray-900 dark:text-white tabular-nums">
                    = <PriceWithCompare price={props.price} compareAt={compareAtTotal(item, selection)} locale={locale} />
                  </p>
                )}
              </div>
              {!opts.deliveryEstimate && (
                <p className="text-[13px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-xl px-3 py-2.5">
                  {t('extras.form.itemDelivery')}
                </p>
              )}
            </>
          )}

          {opts.cleaningVariants && opts.cleaningVariants.length > 0 && (
            <div>
              <p className={LABEL}>{t('extras.form.variant')}</p>
              <div className="space-y-2">
                {opts.cleaningVariants.map((v) => {
                  const on = selection.cleaningVariant === v.id;
                  return (
                    <VariantRow
                      key={v.id}
                      kind="cleaning"
                      name={v.name}
                      imageUrl={v.imageUrl}
                      description={v.description}
                      selected={on}
                      open={openVariant === v.id}
                      onTap={() => { onChange({ cleaningVariant: v.id }); if (!on) setOpenVariant(v.id); else toggleOpen(v.id); }}
                      price={formatEuro(v.price, locale)}
                      control={<SelectedMark on={on} />}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {opts.cleaningDates && opts.cleaningDates.length > 0 && (
            <div>
              <p className={LABEL}>{t('extras.form.days')}</p>
              <div className="flex flex-wrap gap-2">
                {opts.cleaningDates.map((day) => {
                  const active = (selection.cleaningDates || []).indexOf(day) >= 0;
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDate(day)}
                      className={`px-4 py-2 rounded-xl text-[15px] font-semibold border-2 ${active ? CHIP_ON : CHIP_OFF}`}
                    >
                      {new Intl.DateTimeFormat(intlLocale(locale), { weekday: 'short', day: 'numeric', month: 'short' })
                        .format(new Date(`${day}T12:00:00Z`))}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Niente campo quantità per i prezzi "a persona/a notte" né per
              confezioni e articoli, che hanno già il loro contatore sopra. */}
          {opts.allowMultipleQuantity && !opts.cleaningDates && !multipliedByStay && !wineVariants && !bundleVariants && !itemVariants && (
            <div>
              <div className="flex items-center justify-between">
                <p className={`${LABEL} mb-0`}>{t('extras.form.quantity')}</p>
                <Stepper
                  value={quantityValue}
                  min={1}
                  max={quantityMax}
                  onChange={(n) => onChange({ quantity: Math.max(1, Math.min(quantityMax, n)) })}
                  labelMinus={t('common.decrease')}
                  labelPlus={t('common.increase')}
                />
              </div>
              {/* Quanti pezzi restano per QUESTE date: è il tetto dello
                  stepper, e senza scriverlo il "+" spento sembra un guasto. */}
              {stock !== null && stock > 0 && (
                <p className="mt-2 text-[13px] text-gray-500 dark:text-gray-400">
                  {t('extras.inventory.forYourDates', { n: stock })}
                </p>
              )}
              {/* Magazzino (tarallini & co.): "Ultimi 2" quando sono pochi. */}
              {lowStockLeft({ stockLeft: opts.stockLeft }) !== null && (
                <p className="mt-2 text-[13px] font-semibold text-amber-700 dark:text-amber-300">
                  {t('extras.stock.last', { count: opts.stockLeft })}
                </p>
              )}
            </div>
          )}

          {opts.requiresNotes && (
            <div>
              <p className={LABEL}>{t('extras.form.notes')}</p>
              <textarea
                rows={2}
                value={selection.notes || ''}
                onChange={(e) => onChange({ notes: e.target.value })}
                className={INPUT}
                placeholder={t('extras.form.notesPlaceholder')}
              />
            </div>
          )}
        </div>
      )}

      {!hideAction && <ExtraActions {...props} />}
    </>
  );
};

// ------------------------------------------------------------------
// L'AZIONE: compra, chiedi, oppure "non si può". Una sola copia, usata
// dentro il modulo e dalla barra fissa in fondo alla scheda.
// ------------------------------------------------------------------

export const ExtraActions: React.FC<ExtraServiceFormProps & {
  /** Cosa mostrare a sinistra del bottone (il totale, nella barra fissa). */
  priceSlot?: React.ReactNode;
  /** Il testo del bottone d'acquisto, quando non è "Acquista" (es. "Aggiungi al carrello"). */
  buyLabelOverride?: string;
  /** Senza margine sopra (dentro la barra fissa). */
  flush?: boolean;
}> = ({
  item, selection, onChange, price, locale, canBuy, busy, requested, requestState, requestErrorText,
  transferAction, onBuy, onRequest, priceSlot, buyLabelOverride, flush,
}) => {
  const top = flush ? '' : 'mt-4';
  const { t } = useTranslation();
  const transfer = item.options.transfer;
  const notSellable = notSellableInApp(item);
  const stock = stockOf(item);
  const sent = requested || requestState === 'sent';
  // Con il totale già scritto accanto (barra fissa) il bottone non lo ripete.
  const buyLabel = busy
    ? t('extras.confirming')
    : buyLabelOverride
      ? buyLabelOverride
      : price > 0 && !priceSlot ? `${t('extras.buy')} · ${formatEuro(price, locale)}` : t('extras.buy');

  const sentNote = (
    <p className="text-[13px] text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl px-3 py-2.5">
      {t('extras.requestSent')}
    </p>
  );
  const errorNote = requestState === 'error' ? (
    <p className="mt-2 text-[13px] text-red-600 dark:text-red-400">{requestErrorText || t('extras.requestError')}</p>
  ) : null;
  const row = (button: React.ReactNode) => (
    <div className="flex items-center gap-3">
      {priceSlot}
      {button}
    </div>
  );

  if (item.available && notSellable) {
    return (
      <p className={`${top} text-[13px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-xl px-3 py-2.5`}>
        {t('extras.notSellableInApp')}
      </p>
    );
  }

  // Transfer: un bottone solo, che cambia mestiere col termine.
  if (transfer && (item.available || item.canRequest) && !item.alreadyPurchased) {
    return (
      <div className={top}>
        {sent ? sentNote : (
          <>
            {transferAction === 'request' && (
              <p className="mb-2 text-[13px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-xl px-3 py-2.5">
                {t('extras.reasons.after_cutoff')}
              </p>
            )}
            {row(
              <button
                type="button"
                disabled={!canBuy || busy || requestState === 'sending'}
                onClick={transferAction === 'request' ? onRequest : onBuy}
                className={`flex-1 py-3.5 rounded-2xl text-[15px] font-bold disabled:opacity-50 ${
                  transferAction === 'request'
                    ? 'border-2 border-amber-500 text-amber-700 dark:text-amber-300'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-orange-500/25'
                }`}
              >
                {!transferLegChosen(selection)
                  ? t('extras.transfer.chooseLeg')
                  : transferAction === 'request'
                    ? (requestState === 'sending' ? t('extras.requesting') : t('extras.requestSubmit'))
                    : buyLabel}
              </button>,
            )}
          </>
        )}
        {errorNote}
      </div>
    );
  }

  if (item.available && !notSellable && !transfer && stock !== 0) {
    return (
      <div className={top}>
        {row(
          <button
            type="button"
            disabled={!canBuy || busy}
            onClick={onBuy}
            className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-50 text-white text-[15px] font-bold shadow-lg shadow-orange-500/25"
          >
            {buyLabel}
          </button>,
        )}
      </div>
    );
  }

  if (!item.available && item.canRequest && !transfer) {
    return (
      <div className={top}>
        {sent ? sentNote : (
          <>
            {/* Chi chiede la culla fuori termine ha quasi sempre qualcosa da
                aggiungere: il campo c'è, ma non blocca l'invio. */}
            <div className="mb-3">
              <p className={LABEL}>{t('extras.form.noteOptional')}</p>
              <textarea
                rows={2}
                value={selection.notes || ''}
                onChange={(e) => onChange({ notes: e.target.value })}
                className={INPUT}
                placeholder={t('extras.form.notesPlaceholder')}
              />
            </div>
            {row(
              <button
                type="button"
                disabled={requestState === 'sending' || (item.serviceId === CUSTOM_TAXI_ID && !customTaxiComplete(selection))}
                onClick={onRequest}
                className="flex-1 py-3.5 rounded-2xl border-2 border-amber-500 text-amber-700 dark:text-amber-300 text-[15px] font-bold disabled:opacity-50"
              >
                {requestState === 'sending' ? t('extras.requesting') : t('extras.requestSubmit')}
              </button>,
            )}
          </>
        )}
        {errorNote}
      </div>
    );
  }

  return null;
};

/** C'è qualcosa da premere per questo servizio? (decide se mostrare la barra fissa) */
export const hasExtraAction = (item: AppExtra): boolean => {
  const transfer = item.options.transfer;
  if (item.alreadyPurchased) return false;
  if (transfer) return item.available || item.canRequest;
  if (item.available) return !notSellableInApp(item) && stockOf(item) !== 0;
  return item.canRequest;
};

// ------------------------------------------------------------------
// IL FORM DEL TRANSFER
// ------------------------------------------------------------------

/**
 * Lo stesso del sito, ridotto all'essenziale del telefono: quali tratte,
 * quando, con che volo (o da quale stazione) e una nota. Arrivo e partenza
 * sono due grandi riquadri: si accende quello che serve (anche entrambi) e
 * sotto compaiono solo i campi di quella tratta.
 *
 * Il prezzo mostrato lo calcola src/lib/transferPricing.ts con la STESSA
 * regola del server, così la cifra vista è quella addebitata.
 */
/** Mete frequenti da Mola: un tocco e la meta è scritta (si può sempre cambiare). */
const CUSTOM_TAXI_SUGGESTIONS = ['Alberobello', 'Ostuni', 'Matera', 'Locorotondo', 'Castellana Grotte', 'Polignano a Mare', 'Monopoli', 'Bari centro'];
const MAX_TAXI_PEOPLE = 12;

const CustomTaxiForm: React.FC<{
  selection: ExtraSelection;
  onChange: (patch: Partial<ExtraSelection>) => void;
  stayFrom?: string;
  stayTo?: string;
}> = ({ selection, onChange, stayFrom, stayTo }) => {
  const { t } = useTranslation();
  const from = String(stayFrom || '').slice(0, 10);
  const to = String(stayTo || '').slice(0, 10);
  const today = romeToday();
  // Solo i giorni del soggiorno, e non quelli già passati.
  const days = from && to ? dayRange(from > today ? from : today, to, 31) : [];
  const people = Math.max(1, Number(selection.people) || 2);
  const roundTrip = !!selection.roundTrip;
  return (
    <div className="mt-4 space-y-4">
      <div>
        <p className={LABEL}>{t('extras.customTaxi.destination')}</p>
        <input
          type="text"
          value={selection.destination || ''}
          maxLength={80}
          onChange={(e) => onChange({ destination: e.target.value })}
          placeholder={t('extras.customTaxi.destinationPlaceholder')}
          className={INPUT}
        />
        <div className="flex gap-2 overflow-x-auto pb-1 mt-2 -mx-1 px-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {CUSTOM_TAXI_SUGGESTIONS.map((place) => {
            const on = (selection.destination || '').trim() === place;
            return (
              <button key={place} type="button" onClick={() => onChange({ destination: place })} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[13px] font-semibold border-2 ${on ? CHIP_ON : CHIP_OFF}`}>
                {place}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <p className={LABEL}>{t('extras.form.day')}</p>
        {days.length > 0 ? (
          <DayChips days={days} value={selection.transferDate} onChange={(day) => onChange({ transferDate: day })} today={today} />
        ) : (
          <p className="text-[13px] text-gray-500 dark:text-gray-400">{t('excursionPlanner.noDates')}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>{t('extras.customTaxi.time')}</span>
          <input type="time" value={selection.transferTime || ''} onChange={(e) => onChange({ transferTime: e.target.value })} className={INPUT} />
        </label>
        <div>
          <p className={LABEL}>{t('extras.customTaxi.people')}</p>
          <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
            <button type="button" aria-label="−" disabled={people <= 1} onClick={() => onChange({ people: Math.max(1, people - 1) })} className="w-10 h-10 flex items-center justify-center text-gray-700 dark:text-gray-200 disabled:opacity-30"><Minus className="h-4 w-4" /></button>
            <span className="w-10 text-center text-[15px] font-bold text-gray-900 dark:text-white tabular-nums bg-gray-50 dark:bg-gray-800 h-10 leading-10">{people}</span>
            <button type="button" aria-label="+" disabled={people >= MAX_TAXI_PEOPLE} onClick={() => onChange({ people: Math.min(MAX_TAXI_PEOPLE, people + 1) })} className="w-10 h-10 flex items-center justify-center text-gray-700 dark:text-gray-200 disabled:opacity-30"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      <div>
        <div className="flex gap-2">
          <button type="button" onClick={() => onChange({ roundTrip: false, returnTime: undefined })} className={`flex-1 py-2.5 rounded-xl text-[14px] font-semibold border-2 ${!roundTrip ? CHIP_ON : CHIP_OFF}`}>{t('extras.customTaxi.oneWay')}</button>
          <button type="button" onClick={() => onChange({ roundTrip: true })} className={`flex-1 py-2.5 rounded-xl text-[14px] font-semibold border-2 ${roundTrip ? CHIP_ON : CHIP_OFF}`}>{t('extras.customTaxi.roundTrip')}</button>
        </div>
        {roundTrip && (
          <label className="block mt-3">
            <span className={LABEL}>{t('extras.customTaxi.returnTime')}</span>
            <input type="time" value={selection.returnTime || ''} onChange={(e) => onChange({ returnTime: e.target.value })} className={INPUT} />
          </label>
        )}
      </div>
      <p className="text-[13px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-xl px-3 py-2.5">{t('extras.customTaxi.hint')}</p>
    </div>
  );
};

export const TransferForm: React.FC<{
  serviceId: string;
  options: TransferOptions;
  selection: TransferSelection;
  onChange: (patch: Partial<ExtraCheckoutDetails & { quantity?: number }>) => void;
  locale: string;
  price: number;
  dateLimits?: TransferDateLimits;
}> = ({ serviceId, options, selection, onChange, locale, price, dateLimits }) => {
  const { t } = useTranslation();
  const isAirport = serviceId === 'airport_transfer';
  const quote = quoteTransfer(serviceId, options, selection);
  const Far = isAirport ? Plane : TrainFront;

  /**
   * CHI È GIÀ IN CASA NON HA UN ARRIVO: quando il server dice che la tratta
   * di andata non è più selezionabile, il riquadro sparisce e la PARTENZA si
   * preseleziona.
   */
  const arrivalAllowed = options.arrivalAllowed !== false;
  useEffect(() => {
    if (arrivalAllowed) return;
    if (selection.isArrival || !selection.isDeparture) {
      onChange({ isArrival: false, isDeparture: true });
    }
  }, [arrivalAllowed, selection.isArrival, selection.isDeparture, onChange]);

  // I giorni ammessi per l'aereo: arrivo il giorno del check-in o il
  // successivo, partenza il giorno del check-out o il successivo.
  const arrivalDays = dateLimits?.arrivalMin ? dayRange(dateLimits.arrivalMin, dateLimits.arrivalMax || dateLimits.arrivalMin, 3) : [];
  const departureDays = dateLimits?.departureMin ? dayRange(dateLimits.departureMin, dateLimits.departureMax || dateLimits.departureMin, 3) : [];
  const dayOf = (v?: string) => (/^\d{4}-\d{2}-\d{2}/.test(String(v || '')) ? String(v).slice(0, 10) : '');
  const timeOf = (v?: string) => { const m = /(\d{2}:\d{2})$/.exec(String(v || '')); return m ? m[1] : ''; };

  const arrivalLegPrice = isAirport ? options.arrivalPrice : stationPrice(options, selection.stationName);
  const departureLegPrice = isAirport ? options.departurePrice : stationPrice(options, selection.departureStationName);

  const legCard = (args: {
    on: boolean; open: boolean; onToggle: () => void; title: string; hint: string; legPrice: number;
    from: React.ElementType; to: React.ElementType; children?: React.ReactNode;
  }) => {
    const From = args.from;
    const To = args.to;
    return (
      <div className={`rounded-2xl border-2 ${args.on ? 'border-sky-500 bg-sky-50/60 dark:bg-sky-900/15' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'}`}>
        <button type="button" onClick={args.onToggle} className="w-full flex items-center gap-3 p-3 text-left" aria-pressed={args.on}>
          <span className={`flex items-center gap-1 rounded-xl px-2.5 py-2 ${args.on ? 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
            <From className="h-4 w-4" /><span className="text-xs font-bold">→</span><To className="h-4 w-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-bold text-gray-900 dark:text-white">{args.title}</span>
            <span className="block text-[13px] text-gray-500 dark:text-gray-400">{args.hint}</span>
            {!args.open && (
              <span className="block text-xs font-semibold text-amber-600 dark:text-amber-400 mt-0.5">{t('extras.transfer.legAfterCutoff')}</span>
            )}
          </span>
          {args.legPrice > 0 && <span className="text-[15px] font-bold text-gray-900 dark:text-white tabular-nums">{formatEuro(args.legPrice, locale)}</span>}
          <SelectedMark on={args.on} />
        </button>
        {args.on && args.children && <div className="px-3 pb-3 space-y-2">{args.children}</div>}
      </div>
    );
  };

  const stationSelect = (value: string | undefined, onPick: (name: string) => void) => (
    <select value={value || ''} onChange={(e) => onPick(e.target.value)} className={INPUT}>
      <option value="">{t('extras.transfer.selectStation')}</option>
      {options.stationOptions.map((st) => (
        <option key={st.name} value={st.name}>{st.name} · {formatEuro(st.price, locale)}</option>
      ))}
    </select>
  );

  return (
    <div className="mt-4 space-y-3">
      {options.maxPersonsPerTransfer > 0 && (
        <p className="text-[13px] text-gray-500 dark:text-gray-400">
          {t('extras.transfer.maxPersons', { n: options.maxPersonsPerTransfer })}
          {options.vehicles > 1 && ` · ${t('extras.transfer.vehicles', { n: options.vehicles })}`}
        </p>
      )}

      {/* ---- ARRIVO ---- */}
      {arrivalAllowed ? legCard({
        on: !!selection.isArrival,
        open: options.arrivalOpen,
        onToggle: () => onChange({ isArrival: !selection.isArrival }),
        title: t('extras.transfer.arrival'),
        hint: t(isAirport ? 'extras.transfer.legs.airportArrival' : 'extras.transfer.legs.stationArrival'),
        legPrice: arrivalLegPrice,
        from: Far,
        to: HomeIcon,
        children: isAirport ? (
          <>
            {options.requiresFlightInfo && (
              <input
                type="text"
                value={selection.flightNumber || ''}
                onChange={(e) => onChange({ flightNumber: e.target.value })}
                placeholder={t('extras.transfer.flightNumber')}
                className={INPUT}
              />
            )}
            {/* Il GIORNO si sceglie fra quelli ammessi (chip), l'ORA a parte:
                un datetime-local con min/max su iPhone lascia scegliere
                qualunque data. */}
            <div>
              <span className={LABEL}>{t('extras.form.day')}</span>
              <DayChips
                days={arrivalDays}
                value={dayOf(selection.arrivalDateTime)}
                onChange={(day) => onChange({ arrivalDateTime: `${day}T${timeOf(selection.arrivalDateTime)}` })}
              />
            </div>
            <label className="block">
              <span className={LABEL}>{t('extras.transfer.arrivalTimeLabel')}</span>
              <input
                type="time"
                value={timeOf(selection.arrivalDateTime)}
                onChange={(e) => onChange({ arrivalDateTime: `${dayOf(selection.arrivalDateTime) || arrivalDays[0] || ''}T${e.target.value}` })}
                className={INPUT}
              />
            </label>
            <p className="text-xs text-gray-400">{t('extras.transfer.arrivalDateHint')}</p>
          </>
        ) : (
          <>
            {stationSelect(selection.stationName, (name) => onChange({ stationName: name }))}
            <label className="block">
              <span className={LABEL}>{t('extras.transfer.time')}</span>
              <input type="time" value={selection.arrivalTime || ''} onChange={(e) => onChange({ arrivalTime: e.target.value })} className={INPUT} />
            </label>
          </>
        ),
      }) : (
        <p className="text-[13px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 rounded-xl px-3 py-2.5">
          {t('extras.transfer.arrivalNotAvailable')}
        </p>
      )}

      {/* ---- PARTENZA ---- */}
      {options.hasRoundTrip && legCard({
        on: !!selection.isDeparture,
        open: options.departureOpen,
        onToggle: () => onChange({ isDeparture: !selection.isDeparture }),
        title: t('extras.transfer.departure'),
        hint: t(isAirport ? 'extras.transfer.legs.airportDeparture' : 'extras.transfer.legs.stationDeparture'),
        legPrice: departureLegPrice,
        from: HomeIcon,
        to: Far,
        children: isAirport ? (
          <>
            {options.requiresFlightInfo && (
              <input
                type="text"
                value={selection.departureFlightNumber || ''}
                onChange={(e) => onChange({ departureFlightNumber: e.target.value })}
                placeholder={t('extras.transfer.flightNumber')}
                className={INPUT}
              />
            )}
            <div>
              <span className={LABEL}>{t('extras.form.day')}</span>
              <DayChips
                days={departureDays}
                value={dayOf(selection.departureDateTime)}
                onChange={(day) => onChange({ departureDateTime: `${day}T${timeOf(selection.departureDateTime)}` })}
              />
            </div>
            <label className="block">
              <span className={LABEL}>{t('extras.transfer.departureTimeLabel')}</span>
              <input
                type="time"
                value={timeOf(selection.departureDateTime)}
                onChange={(e) => onChange({ departureDateTime: `${dayOf(selection.departureDateTime) || departureDays[0] || ''}T${e.target.value}` })}
                className={INPUT}
              />
            </label>
            <p className="text-xs text-gray-400">{t('extras.transfer.departureDateHint')}</p>
          </>
        ) : (
          <>
            {stationSelect(selection.departureStationName, (name) => onChange({ departureStationName: name }))}
            <label className="block">
              <span className={LABEL}>{t('extras.transfer.time')}</span>
              <input type="time" value={selection.departureTime || ''} onChange={(e) => onChange({ departureTime: e.target.value })} className={INPUT} />
            </label>
          </>
        ),
      })}

      {/* ---- Note ---- */}
      <div>
        <p className={LABEL}>{t('extras.form.noteOptional')}</p>
        <textarea
          rows={2}
          value={selection.notes || ''}
          onChange={(e) => onChange({ notes: e.target.value })}
          className={INPUT}
          placeholder={t('extras.transfer.notesPlaceholder')}
        />
      </div>

      {/* ---- Il conto, prima di pagare ---- */}
      {quote.trips > 0 && (
        <div className="rounded-2xl bg-gray-50 dark:bg-gray-900/40 px-3.5 py-3 space-y-1 text-[14px] text-gray-600 dark:text-gray-300">
          {selection.isArrival && arrivalLegPrice > 0 && (
            <div className="flex justify-between"><span>{t('extras.transfer.arrival')}</span><span className="tabular-nums">{formatEuro(arrivalLegPrice, locale)}</span></div>
          )}
          {selection.isDeparture && departureLegPrice > 0 && (
            <div className="flex justify-between"><span>{t('extras.transfer.departure')}</span><span className="tabular-nums">{formatEuro(departureLegPrice, locale)}</span></div>
          )}
          {quote.nightSurcharge > 0 && (
            <div className="flex justify-between"><span>🌙 {t('extras.transfer.nightSurcharge', { amount: formatEuro(quote.nightSurcharge, locale) })}</span></div>
          )}
          <div className="flex justify-between pt-1 border-t border-gray-200 dark:border-gray-700 text-[15px] font-bold text-gray-900 dark:text-white">
            <span>{t('extras.transfer.total')}</span><span className="tabular-nums">{formatEuro(price, locale)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtraServiceForm;
